import { useState, useEffect, useRef, useCallback } from "react";
import {
    Container, Typography, Paper, Box, TextField, Button, Avatar,
    Grid, Card, CardContent, Divider, IconButton, Snackbar, Alert,
    CircularProgress, alpha, useTheme
} from "@mui/material";
import { useAuth } from '@vortx/shared-ui';
import { useThemeMode } from '@vortx/shared-ui';
import { vortxColors, vortxTypography } from '@vortx/shared-ui';
import { API_BASE_URL } from '../services/api';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import FolderIcon from '@mui/icons-material/Folder';
import ScienceIcon from '@mui/icons-material/Science';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import BusinessIcon from '@mui/icons-material/Business';
import EmailIcon from '@mui/icons-material/Email';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

export default function Profile() {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        avatar_url: ""
    });
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
    const fileInputRef = useRef(null);

    const { currentUser } = useAuth();
    useThemeMode();
    const theme = useTheme();

    // Theme-aware colors (now using standard palette)
    const bgMain = theme.palette.background.default;
    const bgCard = theme.palette.background.paper;
    const textPrimary = theme.palette.text.primary;
    const textSecondary = theme.palette.text.secondary;

    // Get auth headers
    const getHeaders = useCallback(async () => {
        const headers = {
            'Content-Type': 'application/json'
        };
        if (currentUser) {
            const token = await currentUser.getIdToken();
            headers['Authorization'] = `Bearer ${token}`;
        }
        return headers;
    }, [currentUser]);

    // Load profile data
    const loadProfile = useCallback(async () => {
        try {
            setLoading(true);
            const headers = await getHeaders();
            const res = await fetch(`${API_BASE_URL}/api/site_dna/v1/profile`, { headers });
            if (res.ok) {
                const data = await res.json();
                setProfile(data);
                setFormData({
                    name: data.name || "",
                    avatar_url: data.avatar_url || ""
                });
            } else {
                console.error("Failed to load profile:", res.status);
                setSnackbar({ open: true, message: "Failed to load profile", severity: "error" });
            }
        } catch (error) {
            console.error("Error loading profile:", error);
            setSnackbar({ open: true, message: "Error loading profile", severity: "error" });
        } finally {
            setLoading(false);
        }
    }, [getHeaders]);

    useEffect(() => {
        loadProfile();
    }, [loadProfile]);

    const handleSave = async () => {
        try {
            setSaving(true);
            const headers = await getHeaders();
            const res = await fetch(`${API_BASE_URL}/api/user/profile`, {
                method: "PUT",
                headers,
                body: JSON.stringify(formData)
            });

            if (res.ok) {
                const data = await res.json();
                setProfile(data);
                setEditing(false);
                setSnackbar({ open: true, message: "Profile updated successfully", severity: "success" });
            } else {
                const errorText = await res.text();
                console.error("Failed to save profile:", res.status, errorText);
                setSnackbar({ open: true, message: "Failed to save profile", severity: "error" });
            }
        } catch (error) {
            console.error("Error saving profile:", error);
            setSnackbar({ open: true, message: "Error saving profile", severity: "error" });
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setFormData({
            name: profile?.name || "",
            avatar_url: profile?.avatar_url || ""
        });
        setEditing(false);
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            setSnackbar({ open: true, message: "Please select an image file", severity: "error" });
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            setSnackbar({ open: true, message: "Image must be less than 5MB", severity: "error" });
            return;
        }

        try {
            setUploading(true);

            // Upload via API endpoint
            const headers = await getHeaders();
            delete headers['Content-Type']; // Let browser set multipart boundary

            const formDataUpload = new FormData();
            formDataUpload.append('avatar', file);

            const response = await fetch(`${API_BASE_URL}/api/user/profile/avatar`, {
                method: 'POST',
                headers: { 'Authorization': headers['Authorization'] },
                body: formDataUpload
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Upload failed');
            }

            const result = await response.json();

            // Update the form data with the new URL
            setFormData(prev => ({ ...prev, avatar_url: result.avatar_url }));
            setSnackbar({ open: true, message: "Photo uploaded successfully", severity: "success" });
        } catch (error) {
            console.error("Error uploading file:", error);
            setSnackbar({ open: true, message: error.message || "Failed to upload photo", severity: "error" });
        } finally {
            setUploading(false);
            // Reset the file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return "—";
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } catch {
            return "—";
        }
    };

    if (loading) {
        return (
            <Box sx={{
                minHeight: '100vh',
                background: bgMain,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <CircularProgress sx={{ color: vortxColors.gold }} />
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', background: bgMain, py: 6 }}>
            <Container maxWidth="lg">
                {/* Header */}
                <Typography
                    variant="h3"
                    sx={{
                        fontFamily: vortxTypography.heading,
                        color: vortxColors.gold,
                        fontWeight: 700,
                        mb: 4,
                        letterSpacing: '0.02em'
                    }}
                >
                    YOUR PROFILE
                </Typography>

                <Grid container spacing={4}>
                    {/* Profile Card */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{
                            p: 4,
                            bgcolor: bgCard,
                            borderRadius: 3,
                            border: `1px solid ${alpha(vortxColors.gold, 0.2)}`,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                                <Avatar
                                    src={profile?.avatar_url}
                                    sx={{
                                        width: 80,
                                        height: 80,
                                        bgcolor: vortxColors.gold,
                                        fontSize: '2rem',
                                        fontFamily: vortxTypography.heading,
                                        fontWeight: 700
                                    }}
                                >
                                    {profile?.name?.[0]?.toUpperCase() || profile?.email?.[0]?.toUpperCase() || '?'}
                                </Avatar>
                                <Box sx={{ ml: 3, flex: 1 }}>
                                    <Typography variant="h5" sx={{
                                        fontFamily: vortxTypography.heading,
                                        color: textPrimary,
                                        fontWeight: 600
                                    }}>
                                        {profile?.name || "No name set"}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                        <EmailIcon sx={{ fontSize: 16, color: textSecondary, mr: 0.5 }} />
                                        <Typography variant="body2" sx={{ color: textSecondary }}>
                                            {profile?.email}
                                        </Typography>
                                    </Box>
                                </Box>
                                {!editing && (
                                    <IconButton
                                        onClick={() => setEditing(true)}
                                        sx={{
                                            color: vortxColors.gold,
                                            '&:hover': { bgcolor: alpha(vortxColors.gold, 0.1) }
                                        }}
                                    >
                                        <EditIcon />
                                    </IconButton>
                                )}
                            </Box>

                            <Divider sx={{ mb: 3, borderColor: alpha(vortxColors.gold, 0.2) }} />

                            {editing ? (
                                <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <TextField
                                        label="Name"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        fullWidth
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                color: textPrimary,
                                                '& fieldset': { borderColor: alpha(vortxColors.gold, 0.3) },
                                                '&:hover fieldset': { borderColor: vortxColors.gold },
                                                '&.Mui-focused fieldset': { borderColor: vortxColors.gold }
                                            },
                                            '& .MuiInputLabel-root': { color: textSecondary },
                                            '& .MuiInputLabel-root.Mui-focused': { color: vortxColors.gold }
                                        }}
                                    />
                                    {/* Company is read-only - assigned by organization admin */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', py: 1 }}>
                                        <BusinessIcon sx={{ color: vortxColors.gold, mr: 2 }} />
                                        <Box>
                                            <Typography variant="caption" sx={{ color: textSecondary }}>
                                                Organization (managed by admin)
                                            </Typography>
                                            <Typography sx={{ color: textPrimary, fontFamily: vortxTypography.body }}>
                                                {profile?.company_name || "Not assigned"}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    {/* Avatar section with preview, upload, and URL input */}
                                    <Box>
                                        <Typography variant="caption" sx={{ color: textSecondary, mb: 1, display: 'block' }}>
                                            Profile Photo
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                            <Avatar
                                                src={formData.avatar_url}
                                                sx={{
                                                    width: 64,
                                                    height: 64,
                                                    bgcolor: vortxColors.gold,
                                                    fontSize: '1.5rem',
                                                    fontFamily: vortxTypography.heading,
                                                    fontWeight: 700
                                                }}
                                            >
                                                {formData.name?.[0]?.toUpperCase() || profile?.email?.[0]?.toUpperCase() || '?'}
                                            </Avatar>
                                            <Box>
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    ref={fileInputRef}
                                                    onChange={handleFileUpload}
                                                    style={{ display: 'none' }}
                                                />
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={uploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                                                    onClick={() => fileInputRef.current?.click()}
                                                    disabled={uploading}
                                                    sx={{
                                                        color: vortxColors.gold,
                                                        borderColor: alpha(vortxColors.gold, 0.5),
                                                        fontFamily: vortxTypography.body,
                                                        textTransform: 'none',
                                                        '&:hover': {
                                                            borderColor: vortxColors.gold,
                                                            bgcolor: alpha(vortxColors.gold, 0.1)
                                                        }
                                                    }}
                                                >
                                                    {uploading ? 'Uploading...' : 'Upload Photo'}
                                                </Button>
                                                <Typography variant="caption" sx={{ display: 'block', color: textSecondary, mt: 0.5 }}>
                                                    Max 5MB • JPG, PNG, GIF
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <TextField
                                            label="Or enter image URL"
                                            value={formData.avatar_url}
                                            onChange={(e) => setFormData({ ...formData, avatar_url: e.target.value })}
                                            fullWidth
                                            size="small"
                                            placeholder="https://example.com/avatar.jpg"
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    color: textPrimary,
                                                    '& fieldset': { borderColor: alpha(vortxColors.gold, 0.3) },
                                                    '&:hover fieldset': { borderColor: vortxColors.gold },
                                                    '&.Mui-focused fieldset': { borderColor: vortxColors.gold }
                                                },
                                                '& .MuiInputLabel-root': { color: textSecondary },
                                                '& .MuiInputLabel-root.Mui-focused': { color: vortxColors.gold }
                                            }}
                                        />
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                                        <Button
                                            variant="contained"
                                            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                                            onClick={handleSave}
                                            disabled={saving}
                                            sx={{
                                                bgcolor: vortxColors.gold,
                                                color: '#FFFFFF',
                                                fontFamily: vortxTypography.body,
                                                fontWeight: 600,
                                                textTransform: 'none',
                                                '&:hover': { bgcolor: alpha(vortxColors.gold, 0.9) }
                                            }}
                                        >
                                            {saving ? "Saving..." : "Save Changes"}
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            startIcon={<CancelIcon />}
                                            onClick={handleCancel}
                                            disabled={saving}
                                            sx={{
                                                color: textSecondary,
                                                borderColor: alpha(textSecondary, 0.5),
                                                fontFamily: vortxTypography.body,
                                                textTransform: 'none',
                                                '&:hover': {
                                                    borderColor: textSecondary,
                                                    bgcolor: alpha(textSecondary, 0.1)
                                                }
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                    </Box>
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <BusinessIcon sx={{ color: vortxColors.gold, mr: 2 }} />
                                        <Box>
                                            <Typography variant="caption" sx={{ color: textSecondary }}>
                                                Organization
                                            </Typography>
                                            <Typography sx={{ color: textPrimary, fontFamily: vortxTypography.body }}>
                                                {profile?.company_name || "Not assigned"}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <CalendarTodayIcon sx={{ color: vortxColors.gold, mr: 2 }} />
                                        <Box>
                                            <Typography variant="caption" sx={{ color: textSecondary }}>
                                                Member Since
                                            </Typography>
                                            <Typography sx={{ color: textPrimary, fontFamily: vortxTypography.body }}>
                                                {formatDate(profile?.created_at)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            )}
                        </Paper>
                    </Grid>

                    {/* Activity Stats Card */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{
                            p: 4,
                            bgcolor: bgCard,
                            borderRadius: 3,
                            border: `1px solid ${alpha(vortxColors.gold, 0.2)}`,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        }}>
                            <Typography variant="h6" sx={{
                                fontFamily: vortxTypography.heading,
                                color: textPrimary,
                                fontWeight: 600,
                                mb: 3
                            }}>
                                Activity Summary
                            </Typography>

                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Card sx={{
                                        bgcolor: alpha(vortxColors.gold, 0.1),
                                        border: 'none',
                                        boxShadow: 'none'
                                    }}>
                                        <CardContent sx={{ display: 'flex', alignItems: 'center', py: 2 }}>
                                            <FolderIcon sx={{ fontSize: 40, color: vortxColors.gold, mr: 2 }} />
                                            <Box>
                                                <Typography variant="h4" sx={{
                                                    fontFamily: vortxTypography.heading,
                                                    fontWeight: 700,
                                                    color: textPrimary
                                                }}>
                                                    {profile?.stats?.projects_created || 0}
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: textSecondary }}>
                                                    Projects Created
                                                </Typography>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12}>
                                    <Card sx={{
                                        bgcolor: alpha(vortxColors.teal, 0.1),
                                        border: 'none',
                                        boxShadow: 'none'
                                    }}>
                                        <CardContent sx={{ display: 'flex', alignItems: 'center', py: 2 }}>
                                            <ScienceIcon sx={{ fontSize: 40, color: vortxColors.teal, mr: 2 }} />
                                            <Box>
                                                <Typography variant="h4" sx={{
                                                    fontFamily: vortxTypography.heading,
                                                    fontWeight: 700,
                                                    color: textPrimary
                                                }}>
                                                    {profile?.stats?.scenarios_created || 0}
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: textSecondary }}>
                                                    Scenarios Built
                                                </Typography>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12}>
                                    <Card sx={{
                                        bgcolor: alpha(vortxColors.darkGreen, 0.1),
                                        border: 'none',
                                        boxShadow: 'none'
                                    }}>
                                        <CardContent sx={{ display: 'flex', alignItems: 'center', py: 2 }}>
                                            <AutoGraphIcon sx={{ fontSize: 40, color: vortxColors.darkGreen, mr: 2 }} />
                                            <Box>
                                                <Typography variant="h4" sx={{
                                                    fontFamily: vortxTypography.heading,
                                                    fontWeight: 700,
                                                    color: textPrimary
                                                }}>
                                                    {profile?.stats?.optimizations_run || 0}
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: textSecondary }}>
                                                    Optimizations Run
                                                </Typography>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>
                </Grid>

                {/* Snackbar */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                >
                    <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Container>
        </Box>
    );
}
